import React from 'react';
import * as yup from 'yup';

import {
  InteractionModalAccordionsHook,
  InteractionModalComponentProps,
  ModalValidationRules,
} from '$/components/Interactions/types';
import { eWebinarIcon } from '$/images/Icons';
import { StyleProps } from '$/components/ui';
import {
  AttendeeCardProps,
  InteractionDisplayState,
  InteractionInfo,
  NoBodyCard,
  OverlayComponentProps,
  OverlayDimensions,
  VideoOverlayTypes,
} from '$/components/Interactions/InteractionInfo';
import {
  AttendeeFragment,
  InteractionDetails,
  InteractionForAttendeeFragment,
  InteractionFragment,
  InteractionOverlaySettings,
  PublicEWebinarFragment,
  RoomType,
} from '$/GraphQL/schema/schema';
import { PlayerMode } from '$/components/WebinarPlayer/types';

import { AnalyticsCardProps } from '../cards/types';
import { NextWebinarOverlay } from './NextWebinarOverlay';
import { useNextWebinarSettings } from './useNextWebinarSettings';
import { NextWebinarCard } from './NextWebinarCard';
import type { InteractionModalTitleProps } from '../types';
import { JOIN_IN_SECS } from './variables';

const AnalyticsNextWebinarCard = React.lazy(() => import('./AnalyticsNextWebinarCard'));
const NextWebinarModalPreview = React.lazy(() => import('./NextWebinarModal'));

const useNextWebinarCalcDimensions = (
  _interaction: InteractionInfo,
  playerWidth: number,
  playerHeight: number,
  _settings?: InteractionOverlaySettings | null
): OverlayDimensions => {
  return {
    x: playerWidth - playerWidth * 0.02 - 20 * 14,
    y: playerHeight - playerHeight * 0.02 - 15 * 14,
    playerWidth,
    playerHeight,
    dynamicSize: true,
  };
};

export class NextWebinarInfo extends InteractionInfo {
  public constructor(
    i: InteractionForAttendeeFragment | InteractionFragment,
    ewebinar?: PublicEWebinarFragment,
    attendee?: AttendeeFragment
  ) {
    super(i, ewebinar, attendee);
  }

  public onlyOneInstance(): boolean {
    return true;
  }

  public detailsDefault(_interactions: InteractionInfo[]): InteractionDetails {
    return {
      title: this.title(),
      description: this.description() || '',
    };
  }

  public hasAnswers(): boolean {
    return true;
  }

  public hasExpiresInField(): boolean {
    return false;
  }

  public hasEndsAt(): boolean {
    return false;
  }

  public icon(): React.FC<StyleProps> {
    return eWebinarIcon;
  }

  public modalValidationRules(): ModalValidationRules {
    return {
      setId: yup.string().nullable().required(),
      buttonText: yup.string().trim().required(),
      description: yup.string().required(),
    };
  }

  public modalTitleComponent(): React.FC<InteractionModalTitleProps> | null {
    return null;
  }

  public useModalPreviewComponent(): React.LazyExoticComponent<
    React.FC<InteractionModalComponentProps>
  > | null {
    return NextWebinarModalPreview;
  }

  public modalSettingsAccordions(): InteractionModalAccordionsHook {
    return useNextWebinarSettings;
  }

  public shouldRemoveAnsweredCard(): boolean {
    return false;
  }

  public cardComponent(): React.FC<AttendeeCardProps> {
    return NextWebinarCard;
  }

  public shouldShowResultState(playbackPosition: number): boolean {
    if (
      !this.reaction?.detailsFields?.nextWebinar ||
      playbackPosition > this.rooms.getRoom(RoomType.Exit).endsAt
    ) {
      return false;
    }

    // show result at the End of presentation
    return playbackPosition >= this.rooms.getRoom(RoomType.Presentation).endsAt - JOIN_IN_SECS; // for additional second
  }

  public isInteractiveOverlay(): boolean {
    return true;
  }

  public hasVideoOverlay(): VideoOverlayTypes | null {
    return 'over-area';
  }

  public staticOverlayPosition(): boolean {
    return true;
  }

  public hasCard(_isPreview?: boolean): boolean {
    return true;
  }

  public renderOverlayComponent(): React.FC<OverlayComponentProps> {
    return NextWebinarOverlay;
  }

  public cardBodyComponent(): React.FC<{}> {
    return NoBodyCard;
  }

  public calcOverlayDimensionsHook(): (
    interaction: InteractionInfo,
    playerWidth: number,
    playerHeight: number,
    settings?: InteractionOverlaySettings | null | undefined
  ) => OverlayDimensions {
    return useNextWebinarCalcDimensions;
  }

  public showOverlayEditor(): boolean {
    return false;
  }

  public cardDisplayState(playbackPosition: number, mode: PlayerMode): InteractionDisplayState {
    if (mode === PlayerMode.edit) {
      return InteractionDisplayState.Normal;
    }

    if (this.shouldShowResultState(playbackPosition)) {
      return InteractionDisplayState.Result;
    }

    if (this.reaction) {
      return InteractionDisplayState.Submitted;
    }

    return InteractionDisplayState.Normal;
  }

  public analyticsCardComponent(): React.LazyExoticComponent<React.FC<AnalyticsCardProps>> {
    return AnalyticsNextWebinarCard;
  }
}
